import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import { identifyVisitor } from "@/hooks/useVisitorTracking";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];

const careerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(100),
  email: z.string().email("Invalid email address").max(255),
  phone: z.string().min(10, "Phone number must be at least 10 digits").max(20),
  position: z.string().min(2, "Please specify the position").max(100),
  coverLetter: z.string().min(50, "Cover letter must be at least 50 characters").max(2000),
  resume: z.any()
    .refine((file) => file?.length > 0, "Resume is required")
    .refine((file) => file?.[0]?.size <= MAX_FILE_SIZE, "File size must be less than 5MB")
    .refine(
      (file) => ACCEPTED_FILE_TYPES.includes(file?.[0]?.type),
      "Only PDF, DOC, and DOCX files are accepted"
    ),
});

type CareerFormData = z.infer<typeof careerSchema>;

const Careers = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CareerFormData>({
    resolver: zodResolver(careerSchema),
  });

  const onSubmit = async (data: CareerFormData) => {
    setIsSubmitting(true);

    try {
      const file = data.resume[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Convert file to base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      await new Promise((resolve, reject) => {
        reader.onload = resolve;
        reader.onerror = reject;
      });

      const base64File = (reader.result as string).split(',')[1];

      // Send email with resume
      const { error } = await supabase.functions.invoke('send-career-application', {
        body: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          position: data.position,
          coverLetter: data.coverLetter,
          resumeFileName: file.name,
          resumeBase64: base64File,
          resumeMimeType: file.type,
        },
      });

      if (error) throw error;

      // Identify visitor with application information
      await identifyVisitor({
        email: data.email,
        name: data.name,
        phone: data.phone,
        conversionType: 'career_application',
      });

      toast.success("Application submitted successfully! We'll be in touch soon.");
      reset();
      setFileName("");
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error submitting application:", error);
      }
      toast.error("Failed to submit application. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Careers - Join Neil AI Global Solutions"
        description="Join our team at Neil AI Global Solutions. We're looking for talented individuals passionate about AI automation and robotics."
      />
      
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-orbitron text-4xl md:text-5xl font-bold mb-4">
              Join Our <span className="text-primary">Team</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Be part of the future of automation. We're looking for passionate individuals 
              who want to revolutionize industries with AI and robotics.
            </p>
          </div>

          <Card className="glass-effect border-[hsl(var(--line))]">
            <CardHeader>
              <CardTitle className="font-orbitron text-2xl">Career Application</CardTitle>
              <CardDescription>
                Fill out the form below and upload your resume. We'll review your application 
                and get back to you soon.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    {...register("name")}
                    placeholder="John Doe"
                    className="bg-background/50"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register("email")}
                      placeholder="john@example.com"
                      className="bg-background/50"
                    />
                    {errors.email && (
                      <p className="text-sm text-destructive">{errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      {...register("phone")}
                      placeholder="(336) 962-4539"
                      className="bg-background/50"
                    />
                    {errors.phone && (
                      <p className="text-sm text-destructive">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Position Applying For *</Label>
                  <Input
                    id="position"
                    {...register("position")}
                    placeholder="e.g., Sales Representative, Service Technician"
                    className="bg-background/50"
                  />
                  {errors.position && (
                    <p className="text-sm text-destructive">{errors.position.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coverLetter">Cover Letter *</Label>
                  <Textarea
                    id="coverLetter"
                    {...register("coverLetter")}
                    placeholder="Tell us why you'd be a great fit for Neil AI Global Solutions..."
                    className="bg-background/50 min-h-[150px]"
                  />
                  {errors.coverLetter && (
                    <p className="text-sm text-destructive">{errors.coverLetter.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resume">Upload Resume * (PDF, DOC, DOCX - Max 5MB)</Label>
                  <div className="relative">
                    <Input
                      id="resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      {...register("resume")}
                      onChange={(e) => {
                        register("resume").onChange(e);
                        const file = e.target.files?.[0];
                        if (file) {
                          setFileName(file.name);
                        }
                      }}
                      className="bg-background/50 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                    />
                    {fileName && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        <span>{fileName}</span>
                      </div>
                    )}
                  </div>
                  {errors.resume && (
                    <p className="text-sm text-destructive">{errors.resume.message as string}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center text-sm text-muted-foreground">
            <p>
              Questions? Email us at{" "}
              <a href="mailto:careers@neilaiglobalsolutionsllc.com" className="text-primary hover:underline">
                careers@neilaiglobalsolutionsllc.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Careers;
